import Vue from 'vue'
import firebase from 'firebase/app'
import { extractActiveAlarms, isAlarmSupportedDevice } from '@/utils/active-alarms'
import { startAlarmLoop, stopAlarmSound } from '@/utils/alarm-sound'
import { subscribeStatus, deviceStatusPath } from '@/services/realtime-status'

// Unsubscribe handles for each device's realtime status subscription, kept
// outside the Vuex state to avoid storing non-serializable objects in the store.
let alarmUnsubs = []

// Tracks the set of active alarm identities already seen per device so a sound
// is only played when a genuinely new alarm appears (not on the initial load).
let knownAlarmKeysByDevice = {}
let primedDevices = {}

// Timestamp (ms) each alarm identity was last seen clearing. A device can
// momentarily drop and re-raise the same alarm within a fraction of a second (a
// "re-flap"), which would otherwise look like a brand new alarm and fire a false
// "Novo alarme!". Identities that reappear within this window are treated as the
// ongoing alarm instead of a new one.
let recentlyClearedAt = {}
const ALARM_REFLAP_WINDOW_MS = 1000

// Watchdog references that keep the realtime subscription healthy on a long
// running, unattended panel.
let connectedRef = null
let connectedCallback = null
let wasConnected = true
let tokenRefreshTimer = null

// Firebase ID tokens expire after ~1h. The compat SDK normally refreshes them
// automatically, but on a tab that stays open for hours (and may be throttled
// while in the background) the refresh can silently fail, leaving the realtime
// listeners attached yet no longer authorized to read — the "silent out of
// sync" symptom. Forcing a refresh on a timer and on every reconnect keeps the
// database authorized so updates never stop flowing.
const TOKEN_REFRESH_INTERVAL_MS = 30 * 60 * 1000

function forceTokenRefresh () {
  const user = firebase.auth().currentUser
  if (!user) {
    return Promise.resolve()
  }
  return user.getIdToken(true).catch(() => {})
}

// Stable identity for an active alarm, independent of its rec_at timestamp.
function alarmIdentity (alarm) {
  return `${alarm.deviceId}|${alarm.alarmKey || ''}|${alarm.code != null ? alarm.code : ''}`
}

// Plays the alert sound and triggers a visual flash when the given device
// gained alarms it didn't have on the previous snapshot. The first snapshot per
// device only primes the cache (so existing alarms on load do not alert).
function notifyNewAlarms (commit, deviceId, alarms) {
  const now = Date.now()
  const currentKeys = new Set(alarms.map(alarmIdentity))
  const previousKeys = knownAlarmKeysByDevice[deviceId]
  knownAlarmKeysByDevice[deviceId] = currentKeys

  // Record identities that just cleared (present before, absent now) so a fast
  // re-appearance can be recognized as a re-flap rather than a new alarm.
  if (previousKeys) {
    previousKeys.forEach(key => {
      if (!currentKeys.has(key)) {
        recentlyClearedAt[key] = now
      }
    })
  }

  if (!primedDevices[deviceId]) {
    primedDevices[deviceId] = true
    return
  }

  const newAlarms = alarms.filter(alarm => {
    const key = alarmIdentity(alarm)
    const isNew = !previousKeys || !previousKeys.has(key)
    if (!isNew) {
      return false
    }
    // Suppress a re-flap: the same identity cleared and came back within the
    // hysteresis window, so treat it as the ongoing alarm, not a new one.
    const clearedAt = recentlyClearedAt[key]
    return clearedAt == null || (now - clearedAt) > ALARM_REFLAP_WINDOW_MS
  })

  // An identity that is active again no longer counts as recently cleared, and
  // stale entries outside the window are pruned to keep the map bounded.
  currentKeys.forEach(key => { delete recentlyClearedAt[key] })
  Object.keys(recentlyClearedAt).forEach(key => {
    if (now - recentlyClearedAt[key] > ALARM_REFLAP_WINDOW_MS) {
      delete recentlyClearedAt[key]
    }
  })

  if (newAlarms.length > 0) {
    const first = newAlarms[0]
    commit('triggerAlarmFlash', {
      deviceDescription: first.deviceDescription || '',
      barnDescription: first.barnDescription || '',
      nucleusDescription: first.nucleusDescription || '',
      propertyDescription: first.propertyDescription || '',
      i18nKey: first.i18nKey || null,
      code: first.code != null ? first.code : null,
      description: first.description || '',
      count: newAlarms.length
    })
  }
}

const state = {
  activeAlarmsByDevice: {},
  // Bumped each time a new alarm appears, so a global overlay can flash.
  alarmFlash: { tick: 0, deviceDescription: '', barnDescription: '', nucleusDescription: '', propertyDescription: '', i18nKey: null, code: null, description: '', count: 0 },
  // Identities the operator has muted. A muted alarm no longer contributes to
  // the looping sound. When the alarm clears it is dropped from this map so a
  // later re-occurrence of the same type re-arms and sounds again.
  mutedIdentities: {}
}

const getters = {
  activeAlarmsList (state) {
    let alarms = []
    Object.keys(state.activeAlarmsByDevice).forEach(deviceId => {
      alarms = alarms.concat(state.activeAlarmsByDevice[deviceId] || [])
    })
    return alarms
      .map(alarm => Object.assign({}, alarm, {
        muted: !!state.mutedIdentities[alarmIdentity(alarm)]
      }))
      .sort((a, b) => {
        const at = new Date(a.recAt || 0).getTime()
        const bt = new Date(b.recAt || 0).getTime()
        return bt - at
      })
  },
  activeAlarmsCount (state, getters) {
    return getters.activeAlarmsList.length
  },
  // True when the given device currently has at least one active alarm.
  deviceHasActiveAlarms: (state) => (deviceId) => {
    return (state.activeAlarmsByDevice[deviceId] || []).length > 0
  },
  // Number of active alarms currently held for the given device.
  deviceActiveAlarmsCount: (state) => (deviceId) => {
    return (state.activeAlarmsByDevice[deviceId] || []).length
  },
  activeAlarmFlash (state) {
    return state.alarmFlash
  },
  // True while at least one active alarm is not muted (the looping sound plays).
  hasAudibleAlarms (state, getters) {
    return getters.activeAlarmsList.some(alarm => !alarm.muted)
  },
  // True when there is at least one active alarm and every one is muted.
  allActiveAlarmsMuted (state, getters) {
    const list = getters.activeAlarmsList
    return list.length > 0 && list.every(alarm => alarm.muted)
  }
}

const mutations = {
  setDeviceActiveAlarms (state, { deviceId, alarms }) {
    Vue.set(state.activeAlarmsByDevice, deviceId, alarms)
  },
  clearActiveAlarms (state) {
    state.activeAlarmsByDevice = {}
  },
  triggerAlarmFlash (state, payload) {
    state.alarmFlash = {
      tick: state.alarmFlash.tick + 1,
      deviceDescription: payload.deviceDescription || '',
      barnDescription: payload.barnDescription || '',
      nucleusDescription: payload.nucleusDescription || '',
      propertyDescription: payload.propertyDescription || '',
      i18nKey: payload.i18nKey || null,
      code: payload.code != null ? payload.code : null,
      description: payload.description || '',
      count: payload.count || 1
    }
  },
  muteAlarmIdentity (state, identity) {
    Vue.set(state.mutedIdentities, identity, true)
  },
  unmuteAlarmIdentity (state, identity) {
    Vue.delete(state.mutedIdentities, identity)
  },
  muteAllActiveAlarms (state) {
    Object.keys(state.activeAlarmsByDevice).forEach(deviceId => {
      (state.activeAlarmsByDevice[deviceId] || []).forEach(alarm => {
        Vue.set(state.mutedIdentities, alarmIdentity(alarm), true)
      })
    })
  },
  reactivateAllAlarms (state) {
    state.mutedIdentities = {}
  },
  // Drops muted identities that are no longer active so a future re-occurrence
  // of the same alarm re-arms and sounds again.
  pruneMutedIdentities (state) {
    const active = new Set()
    Object.keys(state.activeAlarmsByDevice).forEach(deviceId => {
      (state.activeAlarmsByDevice[deviceId] || []).forEach(alarm => {
        active.add(alarmIdentity(alarm))
      })
    })
    Object.keys(state.mutedIdentities).forEach(identity => {
      if (!active.has(identity)) {
        Vue.delete(state.mutedIdentities, identity)
      }
    })
  }
}

const actions = {
  async subscribeActiveAlarms ({ commit, dispatch, rootGetters }) {
    await Promise.all([
      dispatch('loadDevicesV2'),
      dispatch('loadBarnsV2'),
      dispatch('loadDeviceFeaturesV2'),
      dispatch('fetchAlarmTranslations', rootGetters.language)
    ])

    dispatch('unsubscribeActiveAlarms')
    commit('clearActiveAlarms')

    // Only devices on a plan with the alarms feature enabled (premium plans) take
    // part in the realtime subscription, so users without it never receive
    // active alarms, sounds or flashes.
    const alarmEnabledDeviceIds = rootGetters.alarmEnabledDeviceIds || []
    const devices = (rootGetters.loadedDevicesV2 || [])
      .filter(isAlarmSupportedDevice)
      .filter(device => alarmEnabledDeviceIds.indexOf(device.id) !== -1)

    devices.forEach(device => {
      if (!device.mac || !(device.base || device.type)) {
        return
      }

      const path = deviceStatusPath(device.base || device.type, device.mac, 'general')
      const unsubscribe = subscribeStatus(path, general => {
        const alarms = extractActiveAlarms(general, device, rootGetters.loadedAlarmTranslations)
        commit('setDeviceActiveAlarms', { deviceId: device.id, alarms })
        commit('pruneMutedIdentities')
        notifyNewAlarms(commit, device.id, alarms)
        dispatch('evaluateAlarmSound')
      })

      alarmUnsubs.push(unsubscribe)
    })

    // Keep the realtime connection authorized and self-healing for the lifetime
    // of the subscription.
    connectedRef = firebase.database().ref('.info/connected')
    connectedCallback = connectedRef.on('value', snap => {
      const connected = snap.val() === true
      if (connected && !wasConnected) {
        // We just recovered from a disconnect: force a fresh token so the
        // reattached listeners stay authorized.
        forceTokenRefresh()
      }
      wasConnected = connected
    })

    if (tokenRefreshTimer) {
      clearInterval(tokenRefreshTimer)
    }
    tokenRefreshTimer = setInterval(forceTokenRefresh, TOKEN_REFRESH_INTERVAL_MS)
  },
  unsubscribeActiveAlarms ({ commit }) {
    alarmUnsubs.forEach(unsubscribe => unsubscribe())
    alarmUnsubs = []
    knownAlarmKeysByDevice = {}
    primedDevices = {}
    recentlyClearedAt = {}

    if (connectedRef && connectedCallback) {
      connectedRef.off('value', connectedCallback)
    }
    connectedRef = null
    connectedCallback = null
    wasConnected = true

    if (tokenRefreshTimer) {
      clearInterval(tokenRefreshTimer)
      tokenRefreshTimer = null
    }

    stopAlarmSound()
    commit('clearActiveAlarms')
  },
  // Starts or stops the looping alarm tone based on whether any audible
  // (non-muted) active alarm currently exists.
  evaluateAlarmSound ({ getters }) {
    if (getters.hasAudibleAlarms) {
      startAlarmLoop()
    } else {
      stopAlarmSound()
    }
  },
  toggleMuteAlarm ({ state, commit, dispatch }, alarm) {
    const identity = alarmIdentity(alarm)
    if (state.mutedIdentities[identity]) {
      commit('unmuteAlarmIdentity', identity)
    } else {
      commit('muteAlarmIdentity', identity)
    }
    dispatch('evaluateAlarmSound')
  },
  muteAllAlarms ({ commit, dispatch }) {
    commit('muteAllActiveAlarms')
    dispatch('evaluateAlarmSound')
  },
  reactivateAllAlarms ({ commit, dispatch }) {
    commit('reactivateAllAlarms')
    dispatch('evaluateAlarmSound')
  }
}

export default {
  state,
  getters,
  mutations,
  actions
}



// WEBPACK FOOTER //
// ./src/store/active_alarms/index.js